import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { PLAN_META } from '@str/shared';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { CreditsMeter } from '@/components/CreditsMeter';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useCreditsSheet } from '@/components/CreditsSheetHost';
import { useToast } from '@/components/toast';
import { FullScreenSpinner, Logo } from '@/components/ui';
import { authClient } from '@/lib/auth-client';
import { cardExpiryState } from '@/lib/card-on-file';
import { initials } from '@/lib/format';
import { PropertyProvider, useActiveProperty } from '@/lib/property-store';
import { keys, useMe, usePosts, type Me } from '@/lib/queries';
import { mediaStyle } from '@/lib/scenes';
import { FirstProperty } from './FirstProperty';

const ICONS: Record<string, string> = {
  Dashboard: 'M4 13h6V4H4zM14 4h6v5h-6zM14 13h6v7h-6zM4 17h6v3H4z',
  Calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  Create: 'M12 4v4M12 16v4M4 12h4M16 12h4M12 9l1.2 1.8L15 12l-1.8 1.2L12 15l-1.2-1.8L9 12l1.8-1.2z',
  Posts: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  Media: 'M4 5h16v14H4zM4 15l5-5 4 4 3-3 4 4M15 8h.01',
  Channels:
    'M10 14a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1 1M14 10a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l1-1',
};
const NAV = [
  ['Dashboard', '/app'],
  ['Create', '/app/create'],
  ['Calendar', '/app/calendar'],
  ['Posts', '/app/posts'],
  ['Media', '/app/media'],
  ['Channels', '/app/channels'],
] as const;

export function Shell() {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <FullScreenSpinner />;
  const data = me.data;
  if (!data?.user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  // Signed in with nothing to work on yet: still the app, with the one thing to do.
  if (!data.properties.length) return <FirstProperty me={data} />;
  // A host who owns a property pays for it; someone invited onto one works on the owner's plan.
  const owned = data.properties.find((p) => p.role === 'owner');
  if (owned && !data.subscription?.active)
    return <Navigate to={`/onboarding/plan?property=${owned.id}`} replace />;
  // A link that names a property (`?property=<id>`, the emails use it) opens on that property.
  const requested = new URLSearchParams(location.search).get('property');
  return (
    <PropertyProvider properties={data.properties} requested={requested}>
      <ShellInner me={data} />
    </PropertyProvider>
  );
}

function ShellInner({ me }: { me: Me }) {
  const { active, properties, setActive } = useActiveProperty();
  const creditsSheet = useCreditsSheet();
  const posts = usePosts(active.id);
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  // The phone drawer is open only on the location it was opened at, so any navigation closes it:
  // sidebar links, the account menu, and any navigate() from a screen (location.key changes even
  // when the path stays the same).
  const [navOpenAt, setNavOpenAt] = useState<string | null>(null);
  const mobileNav = navOpenAt === location.key;
  const [switcher, setSwitcher] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const shown = useRef<string | null>(null);

  // The provider has taken `?property=`; drop it from the URL, keeping the rest and the state.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('property')) return;
    params.delete('property');
    const search = params.toString();
    void navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state as unknown },
    );
  }, [location, navigate]);

  useEffect(() => {
    const msg = (location.state as { toast?: string } | null)?.toast;
    if (msg && shown.current !== location.key) {
      shown.current = location.key;
      toast(msg);
    }
  }, [location, toast]);

  const scheduledCount = (posts.data ?? []).filter((p) => p.status === 'scheduled').length;
  const grant = me.subscription ? PLAN_META[me.subscription.plan].creditsPerMonth : 0;
  const user = me.user!;
  const planName = me.subscription?.planName ?? '—';
  const pastDue = me.subscription?.status === 'past_due';
  const cardExpired = !!me.subscription && cardExpiryState(me.subscription) === 'expired';
  const signOut = async () => {
    await authClient.signOut();
    qc.clear();
    void navigate('/login', { replace: true });
  };
  const USER_MENU = [
    {
      label: 'Settings',
      to: '/app/settings',
      d: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4 12h2M18 12h2M12 4v2M12 18v2',
    },
    { label: 'Review brand DNA', to: `/review/${active.id}`, d: 'M4 6h16M4 12h10M4 18h7' },
    { label: 'Add a property', to: '/', d: 'M12 5v14M5 12h14' },
  ];

  return (
    <div
      className="app-shell"
      style={{
        flex: 1,
        minWidth: 0,
        height: '100%',
        display: 'flex',
        background: 'var(--bg)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <button
        type="button"
        className="mobile-nav-toggle"
        aria-controls="app-navigation"
        aria-expanded={mobileNav}
        onClick={() => setNavOpenAt(mobileNav ? null : location.key)}
      >
        {mobileNav ? 'Close menu' : 'Menu'}
      </button>
      <aside
        id="app-navigation"
        className={`a-fade app-sidebar${mobileNav ? ' is-open' : ''}`}
        style={{
          width: 216,
          borderRight: '1px solid rgba(0,0,0,.06)',
          padding: '16px 14px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          background: '#fff',
          flex: 'none',
        }}
      >
        <div style={{ padding: '6px 10px 16px' }}>
          <Logo to="/app" size={17} />
        </div>
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <button
            type="button"
            className="hv"
            onClick={() => setSwitcher((s) => !s)}
            aria-haspopup="menu"
            aria-expanded={switcher}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,.1)',
              background: switcher ? 'var(--bg)' : '#fff',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                flex: 'none',
                ...mediaStyle({ url: active.heroUrl, scene: active.heroScene }),
              }}
            />
            <span
              style={{
                flex: 1,
                fontSize: 13,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {active.name}
            </span>
            <span style={{ color: '#999', fontSize: 11 }}>▾</span>
          </button>
          {switcher && (
            <div
              role="menu"
              className="card float a-pop"
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                right: 0,
                zIndex: 30,
                padding: 6,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {properties.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="menuitem"
                  className="nav-item hv"
                  style={{ fontSize: 13, padding: '8px 10px' }}
                  onClick={() => {
                    void setActive(p.id).then((changed) => {
                      if (changed && location.pathname === '/app/create')
                        void navigate('/app/create');
                    });
                    setSwitcher(false);
                  }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      flex: 'none',
                      ...mediaStyle({ url: p.heroUrl, scene: p.heroScene }),
                    }}
                  />
                  <span
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.name}
                  </span>
                  {p.id === active.id && <span style={{ fontSize: 11 }}>✓</span>}
                </button>
              ))}
              <div style={{ height: 1, background: 'var(--line)', margin: '4px 6px' }} />
              <button
                type="button"
                role="menuitem"
                className="nav-item hv"
                style={{ fontSize: 13, padding: '8px 10px' }}
                onClick={() => void navigate(`/review/${active.id}`)}
              >
                Review brand DNA
              </button>
              <button
                type="button"
                role="menuitem"
                className="nav-item hv"
                style={{ fontSize: 13, padding: '8px 10px' }}
                onClick={() => void navigate('/')}
              >
                + Add property
              </button>
            </div>
          )}
        </div>
        {NAV.map(([label, to]) => (
          <NavLink
            key={label}
            to={to}
            end={to === '/app'}
            className={({ isActive }) => `nav-item hv${isActive ? ' on' : ''}`}
            onClick={
              to === '/app/create'
                ? (e) => {
                    // Create always means a new post, even on the Create page mid-way through one.
                    e.preventDefault();
                    void navigate('/app/create', { state: { fresh: crypto.randomUUID() } });
                  }
                : undefined
            }
          >
            {({ isActive }) => (
              <>
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                >
                  <path d={ICONS[label]} />
                </svg>
                {label}
                {label === 'Calendar' && scheduledCount > 0 && (
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '2px 7px',
                      borderRadius: 99,
                      background: isActive ? '#fff' : '#111',
                      color: isActive ? '#111' : '#fff',
                    }}
                  >
                    {scheduledCount}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <CreditsMeter
            credits={me.credits}
            grant={grant}
            onBuy={me.credits ? () => creditsSheet.open() : undefined}
          />
          {creditsSheet.element}
          <div style={{ position: 'relative' }}>
            {userMenu && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                  onClick={() => setUserMenu(false)}
                />
                <div
                  role="menu"
                  className="card float a-rise"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 'calc(100% + 8px)',
                    zIndex: 41,
                    padding: 6,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 10px 12px',
                      borderBottom: '1px solid var(--line)',
                      marginBottom: 4,
                    }}
                  >
                    <span className="avatar" style={{ width: 34, height: 34, fontSize: 12 }}>
                      {initials(user.name) || 'H'}
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        lineHeight: 1.3,
                        minWidth: 0,
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{user.name}</span>
                      <span
                        style={{
                          fontSize: 11.5,
                          color: '#888',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {user.email}
                      </span>
                    </span>
                  </div>
                  {USER_MENU.map((m) => (
                    <button
                      key={m.label}
                      type="button"
                      role="menuitem"
                      className="nav-item hv"
                      style={{ fontSize: 13.5, padding: '9px 10px', borderRadius: 10 }}
                      onClick={() => {
                        setUserMenu(false);
                        void navigate(m.to);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        style={{ color: '#777' }}
                      >
                        <path d={m.d} />
                      </svg>
                      {m.label}
                    </button>
                  ))}
                  <div style={{ height: 1, background: 'var(--line)', margin: '4px 6px' }} />
                  <button
                    type="button"
                    role="menuitem"
                    className="nav-item hv"
                    style={{
                      fontSize: 13.5,
                      padding: '9px 10px',
                      borderRadius: 10,
                      color: '#8b3c1a',
                    }}
                    onClick={() => void signOut()}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    >
                      <path d="M10 4H5v16h5M14 8l4 4-4 4M18 12H9" />
                    </svg>
                    Sign out
                  </button>
                </div>
              </>
            )}
            <button
              type="button"
              className="hv"
              aria-haspopup="menu"
              aria-expanded={userMenu}
              aria-label="Account menu"
              onClick={() => setUserMenu((o) => !o)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 11,
                border: 0,
                width: '100%',
                textAlign: 'left',
                background: userMenu ? 'var(--bg)' : 'transparent',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = userMenu ? 'var(--bg)' : 'transparent')
              }
            >
              <span className="avatar">{initials(user.name) || 'H'}</span>
              <span
                style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0 }}
              >
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.name}
                </span>
                <span style={{ fontSize: 11, color: '#888' }}>{planName}</span>
              </span>
              <span style={{ marginLeft: 'auto', color: '#999', fontSize: 11 }}>
                {userMenu ? '▴' : '▾'}
              </span>
            </button>
          </div>
        </div>
      </aside>
      <main
        className="app-main"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
        onClick={() => switcher && setSwitcher(false)}
      >
        {(pastDue || cardExpired) && location.pathname !== '/app/settings' && (
          <div
            role="alert"
            style={{
              padding: '8px 32px',
              background: '#fbf3ef',
              color: '#8b3c1a',
              fontSize: 12.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>
              {pastDue
                ? 'Your last payment failed. Update your card to keep posting.'
                : 'The card on file has expired. Add a new one before the next charge.'}
            </span>
            <Link to="/app/settings" style={{ fontWeight: 700, color: 'inherit' }}>
              Update card
            </Link>
          </div>
        )}
        {!user.emailVerified && location.pathname === '/app' && (
          <div
            style={{
              padding: '8px 32px',
              background: 'var(--accent-tint)',
              color: 'var(--accent-dark)',
              fontSize: 12.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Confirm your email to get scheduling receipts and booking alerts.</span>
            <button
              type="button"
              className="hv"
              style={{ border: 0, background: 'none', fontWeight: 700, color: 'inherit' }}
              onClick={() =>
                void authClient
                  .sendVerificationEmail({ email: user.email, callbackURL: '/app' })
                  .then(() => {
                    toast('Verification email sent');
                    void qc.invalidateQueries({ queryKey: keys.me });
                  })
              }
            >
              Resend email
            </button>
          </div>
        )}
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
